import type React from 'react';
import { colors } from '../theme/colors';

interface CalendarLegendProps {
  className?: string;
}

export const CalendarLegend: React.FC<CalendarLegendProps> = ({ className }) => {
  return (
    <div
      className={className}
      style={{
        padding: '1rem',
        fontSize: '0.75rem',
        marginTop: '0.5rem',
      }}
    >
      {/* Day Status Section */}
      <h4
        style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: colors.secondary.text,
          marginBottom: '0.5rem',
          marginTop: 0,
        }}
      >
        Day Status
      </h4>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.5rem',
          marginBottom: '1rem',
        }}
      >
        {/* Complete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.green,
              border: `1px solid ${colors.accent.green}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            15
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Complete</span>
        </div>

        {/* No Hours */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.yellow,
              border: `1px solid ${colors.accent.yellow}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            16
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>No Hours</span>
        </div>

        {/* Mismatch */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.red,
              border: `1px solid ${colors.accent.red}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            17
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Mismatch</span>
        </div>

        {/* Holiday */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.gray,
              border: `1px solid ${colors.neutral.grey[300]}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              opacity: 0.7,
            }}
          >
            18
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Holiday</span>
        </div>

        {/* Today */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              position: 'relative',
              width: '1.5rem',
              height: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '0.7rem', fontWeight: '600', border:`2px solid ${colors.accent.blue}50`, padding: '0.25rem', borderRadius: '4px', }}>20</span>
            <div
              style={{
                position: 'absolute',
                width: '1rem',
                height: '2px',
                backgroundColor: colors.accent.blue,
                bottom: '0.15rem',
              }}
            />
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Today</span>
        </div>

        {/* Weekend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span
            style={{
              fontSize: '0.7rem',
              opacity: 0.5,
              width: '1.5rem',
              textAlign: 'center',
            }}
          >
            21
          </span>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Weekend</span>
        </div>

        {/* Absence */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.brown,
              border: `1px solid ${colors.neutral.grey[400]}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            22
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Absence</span>
        </div>

        {/* Vacation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.brownDark,
              border: `1px solid ${colors.neutral.grey[500]}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            23
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Vacation</span>
        </div>

        {/* Bridge Day */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: '#faa1c9',
              border: `1px solid ${colors.neutral.grey[400]}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: '500',
            }}
          >
            24
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Bridge Day</span>
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          height: '1px',
          backgroundColor: colors.neutral.grey[200],
          margin: '0.75rem 0',
        }}
      />

      {/* Week Status Section */}
      <h4
        style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: colors.secondary.text,
          marginBottom: '0.5rem',
          marginTop: 0,
        }}
      >
        Week Status
      </h4>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.5rem',
        }}
      >
        {/* Week Closed */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.greenWeek,
              border: `1px solid ${colors.accent.green}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: '600',
            }}
          >
            CW
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Week Closed</span>
        </div>

        {/* Week Open */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              backgroundColor: colors.pastel.yellowWeek,
              border: `1px solid ${colors.accent.yellow}`,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: '600',
            }}
          >
            CW
          </div>
          <span style={{ fontSize: '0.75rem', color: colors.secondary.text }}>Week Open</span>
        </div>
      </div>
    </div>
  );
};
